/* AUTO-SYNCED from PsyKit.io/components/session/CheckinForm.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import EmotionPicker, { DEFAULT_EMOTIONS } from "@/components/session/EmotionPicker";
import ScaleInput from "@/components/session/ScaleInput";
import type { CheckinLiveData } from "@/lib/checkin-types";

interface CheckinFormProps {
  /** Controlled value — the live, shared check-in answers. */
  value: CheckinLiveData;
  /** Called on EVERY edit. The parent updates local state and broadcasts. */
  onChange: (next: CheckinLiveData) => void;
  emotions?: string[];
  showGoal?: boolean;
  title: string;
  subtitle?: string;
  /** Read-only panel shown beside the form (the pre→post comparison). */
  comparison?: React.ReactNode;
  primaryLabel: string;
  onPrimary: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  busy?: boolean;
  /** Small "shared live" hint under the header. */
  liveHint?: string;
}

/**
 * Controlled check-in form (emotions + distress + capability + optional goal +
 * note). Because it's fully controlled, the same `value` can be mirrored across
 * the therapist and client over Socket.IO — whoever edits, the other sees it.
 *
 * Rendered as a full-screen overlay. When `comparison` is supplied (the post
 * check-in), the form sits in a two-column layout beside the pre-session
 * snapshot so the change across the session is visible.
 */
export default function CheckinForm({
  value,
  onChange,
  emotions = DEFAULT_EMOTIONS,
  showGoal = false,
  title,
  subtitle,
  comparison,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  busy = false,
  liveHint,
}: CheckinFormProps) {
  const set = (patch: Partial<CheckinLiveData>) => onChange({ ...value, ...patch });

  const formFields = (
    <div className="flex flex-col gap-7">
      {showGoal && (
        <div className="flex flex-col gap-2">
          <label className="font-[var(--font-barlow)] text-[13px] uppercase tracking-[0.12em]" style={{ color: "#A0B8D0" }}>
            Session Goal <span style={{ color: "#4A6A88" }}>(optional)</span>
          </label>
          <input
            type="text"
            value={value.goal}
            onChange={(e) => set({ goal: e.target.value })}
            placeholder="What would you like to work on today?"
            maxLength={200}
            className="w-full rounded-[4px] font-[var(--font-dm-mono)] text-[14px] px-4 py-3 outline-none"
            style={{ background: "#060D1A", border: "1px solid #2D4E7A", color: "#C8D4E0" }}
            onFocus={(e) => { e.currentTarget.style.borderColor = "#C9A84C66"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "#2D4E7A"; }}
          />
        </div>
      )}

      <div className="flex flex-col gap-3">
        <label className="font-[var(--font-barlow)] text-[13px] uppercase tracking-[0.12em]" style={{ color: "#A0B8D0" }}>
          How are you feeling{value.phase === "post" ? " now" : " right now"}?{" "}
          <span style={{ color: "#4A6A88" }}>(select all that apply)</span>
        </label>
        <EmotionPicker
          emotions={emotions}
          selected={value.emotions}
          onChange={(em) => set({ emotions: em })}
        />
      </div>

      <ScaleInput
        value={value.distress}
        onChange={(d) => set({ distress: d })}
        label="How much emotional distress does this cause you? (1–10)"
      />
      <ScaleInput
        value={value.capability}
        onChange={(c) => set({ capability: c })}
        label="How capable do you feel to handle it? (1–10)"
      />

      <div className="flex flex-col gap-2">
        <label className="font-[var(--font-barlow)] text-[13px] uppercase tracking-[0.12em]" style={{ color: "#A0B8D0" }}>
          Additional Note <span style={{ color: "#4A6A88" }}>(optional)</span>
        </label>
        <textarea
          value={value.note}
          onChange={(e) => set({ note: e.target.value })}
          placeholder="Anything you'd like to share…"
          rows={3}
          maxLength={1000}
          className="w-full rounded-[4px] font-[var(--font-barlow)] text-[14px] px-4 py-3 outline-none resize-none leading-relaxed"
          style={{ background: "#060D1A", border: "1px solid #2D4E7A", color: "#C8D4E0" }}
          onFocus={(e) => { e.currentTarget.style.borderColor = "#C9A84C66"; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "#2D4E7A"; }}
        />
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto flex items-start justify-center px-6 py-10"
      style={{ background: "rgba(4, 8, 16, 0.96)", backdropFilter: "blur(4px)" }}
    >
      <div
        className={`w-full flex flex-col gap-8 px-10 py-9 rounded-[4px] ${comparison ? "max-w-5xl" : "max-w-3xl"}`}
        style={{ background: "#0A1628", border: "1px solid #2D4E7A", borderLeft: "4px solid #C9A84C" }}
      >
        {/* Header */}
        <div>
          <p className="font-[var(--font-cormorant)] text-4xl font-semibold tracking-wide" style={{ color: "#EEF2F7" }}>
            {title}
          </p>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {subtitle && (
              <p className="font-[var(--font-barlow)] text-[11px] uppercase tracking-[0.2em]" style={{ color: "#5A7A98" }}>
                {subtitle}
              </p>
            )}
            {liveHint && (
              <span className="inline-flex items-center gap-1.5 font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.16em]" style={{ color: "#4a7c59" }}>
                <span className="waiting-pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: "#4a7c59" }} />
                {liveHint}
              </span>
            )}
          </div>
        </div>

        {/* Body — two columns when a comparison is supplied */}
        {comparison ? (
          <div className="grid gap-8 md:grid-cols-2">
            <div
              className="rounded-[4px] px-5 py-5"
              style={{ background: "#070C16", border: "1px solid #1A2E4A" }}
            >
              {comparison}
            </div>
            {formFields}
          </div>
        ) : (
          formFields
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <button
            onClick={onPrimary}
            disabled={busy}
            className="w-full py-4 rounded-[4px] font-[var(--font-barlow)] text-[14px] uppercase tracking-[0.18em] font-semibold transition-all duration-150 disabled:opacity-40"
            style={{ background: "transparent", border: "2px solid #C9A84C", color: "#C9A84C" }}
            onMouseOver={(e) => {
              if (!busy) {
                (e.currentTarget as HTMLButtonElement).style.background = "#C9A84C";
                (e.currentTarget as HTMLButtonElement).style.color = "#060D1A";
              }
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              (e.currentTarget as HTMLButtonElement).style.color = "#C9A84C";
            }}
          >
            {busy ? "Saving…" : primaryLabel}
          </button>
          {secondaryLabel && onSecondary && (
            <button
              onClick={onSecondary}
              disabled={busy}
              className="w-full text-center font-[var(--font-barlow)] text-[11px] uppercase tracking-[0.14em] py-1 transition-colors disabled:opacity-40"
              style={{ background: "none", border: "none", color: "#3D5470", cursor: "pointer" }}
              onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#7A90A8"; }}
              onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#3D5470"; }}
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
